// @see package.json#browser field
import * as Sentry from '@sentry/node';
import Debug from 'debug';
import get from 'lodash/get';
import merge from 'lodash/merge';

import { ApiMiddlewareError } from 'lib/store/middleware/api';

const debug = Debug('rs:utils/monitor');

/**
 * An isomorphic wrapper around Sentry's two JS libraries.
 *
 * Sentry has two libraries:
 *   1. `@sentry/node` (server-side)
 *   2. `@sentry/browser` (client-side)
 *
 * This class exposes a limited API subset to allow ease
 * of maintenance and potentially swapping out Sentry
 * when the next best thing comes along.
 *
 * We've named it 'Monitor' to decouple our app's intent from
 * the specific proprietary library we happen to be using
 * at the time.
 */
class Monitor {
  Sentry = Sentry;

  beforeSend = (event, hint) => {
    const isUnhandledRejection =
      get(hint, 'data.stack.mechanism') === 'onunhandledrejection' || // @sentry/browser
      get(event, 'extra.unhandledPromiseRejection', false); // @sentry/node
    const actionType = get(hint, 'originalException.type');
    const statusCode = get(hint, 'originalException.status');
    if (isUnhandledRejection && actionType) {
      return {
        ...event,
        fingerprint: [actionType, statusCode],
        tags: merge(event.tags || {}, { from: 'Unhandled Rejection' }),
      };
    }
    return event;
  };

  install(dsn, config = {}) {
    if (!dsn) return;

    const { environment, release } = config;
    const initParams = {
      dsn,
      environment,
      beforeSend: this.beforeSend,
    };
    if (release) {
      initParams.release = release;
    }

    Sentry.init(initParams);

    debug('installed: %s', config.environment);
  }

  setUser(email) {
    Sentry.configureScope((scope) => scope.setUser({ email }));
  }

  /**
   * Send an error to the Sentry service.
   *
   * @param {Error|String} error
   * @param {Object} [data]
   */
  error(error, data = {}) {
    debug('track error');

    if (!error) return;

    if (
      error instanceof ApiMiddlewareError &&
      error.action?.status >= 400 &&
      error.action?.status < 500
    ) {
      // these errors should be counted through DogStatsD
      return;
    }

    Sentry.withScope((scope) => {
      if (Object.keys(data.tags).length) {
        Object.entries(data.tags).map(([tag, value]) =>
          scope.setTag(tag, value),
        );
      }
      if (data.extra) {
        scope.setExtra('data', data.extra);
      }
      if (error instanceof ApiMiddlewareError) {
        scope.setContext('action', {
          status: error.action?.status,
          response: error.action?.payload,
        });
      }

      if (typeof error === 'string') Sentry.captureMessage(error);
      else if (error instanceof Error) Sentry.captureException(error);
      else Sentry.captureEvent(error);
    });
  }
}

export default Monitor;
